import React, { useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons in Leaflet when bundled
import L from "leaflet";
const DefaultIcon = L.icon({
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

/**
 * Props:
 *  - value?: { lat, lng, address, accuracy, timestamp }
 *  - onChange: (obj) => void
 *  - onClose?: () => void   // optional when rendered in a modal/sheet
 */
export default function LocationField({ value, onChange, onClose }) {
  const [query, setQuery] = useState("");
  const [pin, setPin] = useState(
    value?.lat && value?.lng ? { lat: value.lat, lng: value.lng } : null
  );
  const [address, setAddress] = useState(value?.address || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [geoAccuracy, setGeoAccuracy] = useState(value?.accuracy ?? null);

  // Initial map center
  const center = useMemo(
    () => pin || { lat: 49.2827, lng: -123.1207 }, // Vancouver fallback
    [pin]
  );

  // Map click handler to move pin
  function ClickToSetPin() {
    useMapEvents({
      click(e) {
        setPin({ lat: e.latlng.lat, lng: e.latlng.lng });
        setGeoAccuracy(null); // manual move → accuracy unknown
        setError("");
      }
    });
    return null;
  }

  const locateMe = () => {
    setLoading(true);
    setError("");
    if (!navigator.geolocation) {
      setLoading(false);
      setError("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setPin({ lat: latitude, lng: longitude });
        setGeoAccuracy(accuracy); // keep reported accuracy (meters)
        // Try reverse geocode to a human address
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
          );
          const json = await res.json();
          setAddress(json.display_name || "");
        } catch {
          // ignore reverse errors
        }
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        setError(err.message || "Unable to get current location.");
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(
          query.trim()
        )}`
      );
      const json = await res.json();
      if (json?.length) {
        const top = json[0];
        const lat = parseFloat(top.lat);
        const lng = parseFloat(top.lon);
        setPin({ lat, lng });
        setGeoAccuracy(null); // search result isn't a GPS fix
        setAddress(top.display_name || query.trim());
      } else {
        setError("No results for that address.");
      }
    } catch (e) {
      setError("Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const saveApply = () => {
    if (!pin) {
      setError("Pick a point on the map or use Locate me.");
      return;
    }
    onChange({
      lat: pin.lat,
      lng: pin.lng,
      address: address || "",
      accuracy: geoAccuracy ?? undefined,
      timestamp: new Date().toISOString()
    });
    onClose?.();
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-3">
        <input
          type="text"
          placeholder="Type address"
          className="flex-1 border rounded-md px-3 py-2"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <button
          onClick={search}
          className="px-3 py-2 rounded-md bg-violet-600 text-white"
          disabled={loading}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      <button
        onClick={locateMe}
        className="w-full mb-3 border rounded-md py-2 hover:bg-gray-50"
        disabled={loading}
      >
        {loading ? "Locating…" : "📍 Locate me"}
      </button>

      <div className="mb-3">
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={pin ? 15 : 11}
          scrollWheelZoom
          className="w-full"
          style={{ height: 320, borderRadius: 12 }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickToSetPin />
          {pin && <Marker position={[pin.lat, pin.lng]} />}
        </MapContainer>
      </div>

      <input
        className="w-full border rounded-md px-3 py-2 mb-3"
        placeholder="Address (optional)"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />

      {error && <div className="text-red-600 text-sm mb-3">{error}</div>}

      <div className="flex gap-2 justify-end">
        {onClose && (
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-md border"
          >
            Cancel
          </button>
        )}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveApply(); }}
          className="px-3 py-2 rounded-md bg-violet-600 text-white"
        >
          Save and apply
        </button>
      </div>
    </div>
  );
}
